import { useEffect, useRef, useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import styled, { keyframes } from "styled-components";
import Pagination from "../../../components/Common/Table/Components/Pagination";
import {
  fetchBrandListPublic,
  fetchCategoryListPublic,
} from "../../../services/inventory.services";
import ProductSearchableDropdown from "../../Inventory/CreateInventory/ProductSearchableDropdown";
import Header from "../components/Header/Header";
import useProductList from "../components/Products/useProductList";
import { ProductItem } from "../components/Products/ProductItem";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ProductsListing = () => {
  const {
    list,
    pagination,
    handleChangePage,
    handleChangeRowsPerPage,
    totalCount,
    handleFilter,
  } = useProductList();
  const [labels, setLabels] = useState({
    brand: "Search brand",
    category: "Search Category",
  });

  const pageRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const page = pageRef.current;
    const hero = heroRef.current;
    const filter = filterRef.current;

    if (!page || !hero || !filter) return;

    const ctx = gsap.context(() => {
      // Hero animations
      gsap.fromTo(
        hero.querySelector(".heroLabel"),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", delay: 0.1 }
      );

      gsap.fromTo(
        hero.querySelector("h1"),
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", delay: 0.2 }
      );

      gsap.fromTo(
        hero.querySelector(".heroDescription"),
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out", delay: 0.4 }
      );

      // Floating shapes
      gsap.fromTo(
        hero.querySelectorAll(".floatingShape"),
        { opacity: 0, scale: 0 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.8,
          stagger: 0.1,
          ease: "back.out(1.7)",
          delay: 0.3,
        }
      );

      // Filter animation
      gsap.fromTo(
        filter,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out", delay: 0.6 }
      );
    }, page);

    return () => ctx.revert();
  }, []);

  // Animate products when list changes
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || list.length === 0) return;

    const cards = grid.querySelectorAll(".product-card");
    gsap.fromTo(
      cards,
      { opacity: 0, y: 40, scale: 0.9 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        stagger: 0.08,
        ease: "power3.out",
      }
    );
  }, [list]);

  const clearFilters = () => {
    setLabels(() => ({
      brand: "Search brand",
      category: "Search Category",
    }));
    handleFilter("brandId", "");
    handleFilter("categoryId", "");
  };

  return (
    <PageWrapper ref={pageRef}>
      <Header />
      <HeroSection ref={heroRef}>
        {/* Background decorations */}
        <div className="heroBg">
          <div className="gradientOrb orb1" />
          <div className="gradientOrb orb2" />
          <div className="gridPattern" />
        </div>

        {/* Floating shapes */}
        <div className="floatingShape shape1" />
        <div className="floatingShape shape2" />
        <div className="floatingShape shape3" />

        <div className="heroContent">
          <div className="heroLabel">
            <span className="line" />
            <span>Explore Collection</span>
            <span className="line" />
          </div>
          <h1>
            Our <span className="highlight">Products</span>
          </h1>
          <p className="heroDescription">
            Discover our curated selection of premium technology products and
            accessories designed to elevate your business operations.
          </p>

          <div className="heroStats">
            <div className="statItem">
              <span className="statNumber">{totalCount}+</span>
              <span className="statLabel">Products</span>
            </div>
            <div className="statDivider" />
            <div className="statItem">
              <span className="statNumber">50+</span>
              <span className="statLabel">Brands</span>
            </div>
            <div className="statDivider" />
            <div className="statItem">
              <span className="statNumber">24/7</span>
              <span className="statLabel">Support</span>
            </div>
          </div>
        </div>
      </HeroSection>

      <ContentWrapper>
        <FilterSection ref={filterRef}>
          <div className="filterHeader">
            <h3>Filter Products</h3>
            <span className="filterCount">{list.length} results</span>
          </div>
          <div className="filterControls">
            <div className="filterItem">
              <label>Brand</label>
              <ProductSearchableDropdown
                placeholder="Search brand"
                fetchList={fetchBrandListPublic}
                key="brand"
                defaultValue={labels?.brand}
                value={labels.brand}
                onHandleChange={(data: any) => {
                  setLabels((label) => ({
                    ...label,
                    brand: data?.label,
                  }));
                  handleFilter("brandId", data?.value);
                }}
              />
            </div>
            <div className="filterItem">
              <label>Category</label>
              <ProductSearchableDropdown
                placeholder="Search Category"
                fetchList={fetchCategoryListPublic}
                key="category"
                defaultValue={labels?.category}
                value={labels.category}
                onHandleChange={(data: any) => {
                  setLabels((label) => ({
                    ...label,
                    category: data?.label,
                  }));
                  handleFilter("categoryId", data?.value);
                }}
              />
            </div>
            <ClearButton onClick={clearFilters}>
              <ClearIcon />
              <span>Clear All</span>
            </ClearButton>
          </div>
        </FilterSection>

        {list.length === 0 ? (
          <EmptyState>
            <div className="emptyIcon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3>No Products Found</h3>
            <p>Try adjusting your filters or search criteria</p>
          </EmptyState>
        ) : (
          <ProductGrid ref={gridRef}>
            {list.map((item, index) => (
              <div className="product-card" key={index}>
                <ProductItem products={item} />
              </div>
            ))}
          </ProductGrid>
        )}

        <PaginationWrapper>
          <Pagination
            count={totalCount}
            rowsPerpage={pagination?.perPage}
            onPageChange={handleChangePage}
            page={pagination?.page - 1}
            rowsPerPageOptions={[8, 24, 48]}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </PaginationWrapper>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default ProductsListing;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.05); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: var(--bg-primary);
`;

const HeroSection = styled.section`
  position: relative;
  padding: 120px 20px 100px;
  overflow: hidden;
  background: var(--bg-secondary);

  .heroBg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    overflow: hidden;

    .gradientOrb {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);

      &.orb1 {
        width: 500px;
        height: 500px;
        background: radial-gradient(
          circle,
          rgba(217, 58, 53, 0.15) 0%,
          transparent 70%
        );
        top: -200px;
        right: -100px;
        animation: ${pulse} 8s ease-in-out infinite;
      }

      &.orb2 {
        width: 400px;
        height: 400px;
        background: radial-gradient(
          circle,
          rgba(43, 59, 124, 0.15) 0%,
          transparent 70%
        );
        bottom: -150px;
        left: -100px;
        animation: ${pulse} 10s ease-in-out infinite reverse;
      }
    }

    .gridPattern {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: linear-gradient(
          var(--text-primary) 1px,
          transparent 1px
        ),
        linear-gradient(90deg, var(--text-primary) 1px, transparent 1px);
      background-size: 60px 60px;
      opacity: 0.02;
    }
  }

  .floatingShape {
    position: absolute;
    border-radius: 20px;
    animation: ${float} 6s ease-in-out infinite;

    &.shape1 {
      width: 80px;
      height: 80px;
      background: linear-gradient(
        135deg,
        var(--accent) 0%,
        transparent 100%
      );
      top: 20%;
      left: 10%;
      opacity: 0.3;
      animation-delay: 0s;
    }

    &.shape2 {
      width: 60px;
      height: 60px;
      background: linear-gradient(
        135deg,
        var(--primary) 0%,
        transparent 100%
      );
      top: 30%;
      right: 15%;
      opacity: 0.25;
      animation-delay: 2s;
      border-radius: 50%;
    }

    &.shape3 {
      width: 100px;
      height: 100px;
      border: 3px solid var(--accent);
      background: transparent;
      bottom: 20%;
      right: 10%;
      opacity: 0.2;
      animation-delay: 4s;
      border-radius: 50%;
    }
  }

  .heroContent {
    max-width: 900px;
    margin: 0 auto;
    text-align: center;
    position: relative;
    z-index: 1;

    .heroLabel {
      display: inline-flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 24px;

      .line {
        width: 40px;
        height: 2px;
        background: var(--accent);
      }

      span:not(.line) {
        font-family: Poppins;
        font-size: 14px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 3px;
        color: var(--accent);
      }
    }

    h1 {
      font-family: Poppins;
      font-size: 72px;
      font-weight: 800;
      line-height: 1.1;
      color: var(--text-primary);
      margin: 0 0 24px;

      .highlight {
        background: linear-gradient(
          135deg,
          var(--accent) 0%,
          var(--primary) 100%
        );
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    }

    .heroDescription {
      font-family: Poppins;
      font-size: 20px;
      line-height: 1.7;
      color: var(--text-secondary);
      max-width: 600px;
      margin: 0 auto 48px;
    }

    .heroStats {
      display: inline-flex;
      align-items: center;
      gap: 32px;
      background: var(--card-bg);
      padding: 24px 48px;
      border-radius: 20px;
      border: 2px solid var(--card-border);
      box-shadow: 0 10px 40px var(--shadow-medium);

      .statItem {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;

        .statNumber {
          font-family: Poppins;
          font-size: 32px;
          font-weight: 800;
          color: var(--accent);
        }

        .statLabel {
          font-family: Poppins;
          font-size: 14px;
          color: var(--text-secondary);
        }
      }

      .statDivider {
        width: 1px;
        height: 40px;
        background: var(--card-border);
      }
    }
  }

  @media (max-width: 768px) {
    padding: 100px 20px 80px;

    .heroContent {
      h1 {
        font-size: 42px;
      }

      .heroDescription {
        font-size: 16px;
      }

      .heroStats {
        flex-direction: column;
        gap: 20px;
        padding: 24px 32px;

        .statDivider {
          width: 80px;
          height: 1px;
        }
      }
    }

    .floatingShape {
      display: none;
    }
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 60px 40px 80px;

  @media (max-width: 768px) {
    padding: 40px 20px 60px;
  }
`;

const FilterSection = styled.div`
  background: var(--card-bg);
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 48px;
  border: 2px solid var(--card-border);
  box-shadow: 0 10px 40px var(--shadow-medium);
  position: relative;
  z-index: 50;

  .filterHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    h3 {
      font-family: Poppins;
      font-size: 20px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0;
    }

    .filterCount {
      font-family: Poppins;
      font-size: 14px;
      color: var(--text-secondary);
      background: var(--bg-secondary);
      padding: 8px 16px;
      border-radius: 20px;
    }
  }

  .filterControls {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 24px;
    align-items: end;

    .filterItem {
      display: flex;
      flex-direction: column;
      gap: 8px;

      label {
        font-family: Poppins;
        font-size: 14px;
        font-weight: 600;
        color: var(--text-primary);
      }
    }
  }

  @media (max-width: 768px) {
    padding: 24px;

    .filterControls {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  background: linear-gradient(135deg, var(--accent) 0%, #c12f2a 100%);
  border: none;
  border-radius: 14px;
  color: #fff;
  font-family: Poppins;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(217, 58, 53, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  margin-bottom: 60px;

  .product-card {
    will-change: transform, opacity;
  }

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin: 0 auto 60px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: var(--card-bg);
  border-radius: 24px;
  border: 2px dashed var(--card-border);
  margin-bottom: 60px;

  .emptyIcon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: var(--bg-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;

    svg {
      width: 40px;
      height: 40px;
      color: var(--text-secondary);
    }
  }

  h3 {
    font-family: Poppins;
    font-size: 24px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 8px;
  }

  p {
    font-family: Poppins;
    font-size: 16px;
    color: var(--text-secondary);
    margin: 0;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0;
`;
